import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import { gql } from '@apollo/client';
import endOfMonth from 'date-fns/endOfMonth';
import range from 'lodash.range';
import Link from 'next/link';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';
import activitySearchParams from '../constants/activitySearchParams';
import parseToDateString from '../constants/parseToDateString';
import { TargetAudienceEnum, useActivityAtlternativesQuery } from '../generated/graphql';
import useQueryParams from '../hooks/useQueryParams';
import removeUndefinedEntries from '../utils/removeUndefinedEntries';
import ActivityHit from './ActivityHit';
import ActivityTile from './ActivityTile';
import ContentWrapper from './ContentWrapper';
import Carousel, { CarouselChild } from './designsystem/Carousel';
import Section from './designsystem/Section';
import { useTileImageSizes } from './designsystem/Tile';
import { useTenantContext } from '../context/TenantContext';
import { useRouter } from 'next/router';
import { Locale } from '../entities/Locale';

export const query = gql`
    query ActivityAtlternatives(
        $params: ActivitySearchParamsInput!
        $limit: Int
        $startDate: String!
        $endDate: String!
    ) {
        activities(params: $params, limit: $limit) {
            hits {
                ...ActivityHit
            }
        }
    }
`;

const LIMIT = 12;

interface Props {
    currentActivitySlug: string;
}

const ActivityAlternatives = ({ currentActivitySlug }: Props) => {
    const [{ startDate, endDate, placeId, targetAudiences, themeSlugs }] = useQueryParams(activitySearchParams);
    const { locale } = useRouter();
    const dateVars = {
        startDate: startDate ?? parseToDateString(new Date()),
        endDate: endDate ?? parseToDateString(endOfMonth(new Date())),
    };
    const [ref, inView] = useInView({ triggerOnce: true, rootMargin: '500px' });
    const { data, loading } = useActivityAtlternativesQuery({
        skip: !inView,
        variables: {
            limit: LIMIT,
            params: {
                ...dateVars,
                targetAudiences: targetAudiences as TargetAudienceEnum[],
                placeId,
                themeSlugs,
            },
            ...dateVars,
        },
        ssr: false,
    });
    const sizes = useTileImageSizes();

    const queryParams = removeUndefinedEntries({ startDate, endDate, targetAudiences, themeSlugs, placeId });

    const hits = data?.activities.hits.filter(activity => activity.slug !== currentActivitySlug);
    const showAlternatives = hits && hits.length > 0;
    const { brandConfig } = useTenantContext();

    return (
        <Section name="alternatives" ref={ref as (el: null) => void}>
            {showAlternatives && (
                <ContentWrapper>
                    <>
                        <Carousel title={<FormattedMessage defaultMessage="Probeer ook eens" />} continuous>
                            {loading &&
                                range(4).map(i => (
                                    <CarouselChild key={i}>
                                        <ActivityTile loading />
                                    </CarouselChild>
                                ))}
                            {!loading &&
                                hits?.map(hit => (
                                    <CarouselChild key={hit.name}>
                                        <ActivityHit sizes={sizes} hit={hit} />
                                    </CarouselChild>
                                ))}
                        </Carousel>

                        <ContentWrapper style={{ paddingTop: 0 }}>
                            <Link
                                passHref
                                href={{
                                    pathname: brandConfig?.customSlugs?.agenda?.[locale as Locale]
                                        ? `/${brandConfig?.customSlugs?.agenda?.[locale as Locale]}`
                                        : '/agenda',
                                    query: { view: startDate === endDate ? 'day' : 'month', ...queryParams },
                                }}
                                legacyBehavior
                            >
                                <Button style={{ display: 'inline-flex' }} variant="outline" as="a">
                                    <FormattedMessage defaultMessage="Meer activiteiten bekijken" />
                                </Button>
                            </Link>
                        </ContentWrapper>
                    </>
                </ContentWrapper>
            )}
        </Section>
    );
};

export default ActivityAlternatives;
